// src/services/cobranzaSnapshotsService.js
//
// Snapshots periódicos del estado de la cartera de cobranza.
// Cada snapshot guarda totales + un map deudaId → métricas, para que el panel
// pueda mostrar diferencias temporales (Δ saldo atrasado vs hace N días, etc).
//
// Colección: cobranza_snapshots/{snapshotId}
//   - id: yyyy-mm-dd (un snapshot por día máximo; sobrescribir reciente)
//   - fechaMs, fechaISO
//   - creadoPor: { uid, email }
//   - totales: { totalCasos, totalSaldoAtrasado, totalSaldoRestante, A, B, C, D, COLA_FINAL }
//   - porDeuda: { [deudaId]: { sa, sr, ma, c, ps, rs } }
//     sa=saldoAtrasado, sr=saldoRestante, ma=mesesAtraso, c=cartera,
//     ps=prioridadScore (SB-U2), rs=recuperabilidadScore (SB-U2)
//
// Tamaño esperado: ~5-10KB por snapshot, bien dentro del límite de 1MB.

import { collection, doc, getDocs, query, orderBy, limit, setDoc, Timestamp } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../Firebase/firebaseconfig";

export const COBRANZA_SNAPSHOTS = "cobranza_snapshots";

const toShortKey = (cartera) => {
  switch (cartera) {
    case "A": return "A";
    case "B": return "B";
    case "C": return "C";
    case "D": return "D";
    case "COLA_FINAL": return "CF";
    case "AL_CORRIENTE": return "AC";
    default: return "?";
  }
};

/**
 * Crea (o sobrescribe) un snapshot del día actual.
 * @param {Array} items — array con perfiles del panel inteligente
 *   Cada item debe tener: id (deudaId), saldoAtrasado, saldoRestante, mesesAtraso, cartera
 */
export const crearSnapshotCobranza = async (items = []) => {
  const auth = getAuth();
  const user = auth.currentUser;
  const hoy = new Date();
  const yyyy = hoy.getFullYear();
  const mm = String(hoy.getMonth() + 1).padStart(2, "0");
  const dd = String(hoy.getDate()).padStart(2, "0");
  const snapshotId = `${yyyy}-${mm}-${dd}`;

  const totales = {
    totalCasos: items.length,
    totalSaldoAtrasado: 0,
    totalSaldoRestante: 0,
    A: 0, B: 0, C: 0, D: 0, COLA_FINAL: 0, AL_CORRIENTE: 0,
  };

  const porDeuda = {};

  for (const it of items) {
    const sa = Number(it.saldoAtrasado || 0);
    const sr = Number(it.saldoRestante || 0);
    const ma = Number(it.mesesAtraso || 0);
    const c = String(it.cartera || "");
    // SB-U2: historial de scores
    const ps = Math.round(Number(it.prioridadScore || 0) * 10) / 10;
    const rs = Math.round(Number(it.recuperabilidadScore || 0) * 10) / 10;

    totales.totalSaldoAtrasado += sa;
    totales.totalSaldoRestante += sr;
    if (c in totales) totales[c] += 1;

    porDeuda[it.id] = { sa, sr, ma, c: toShortKey(c), ps, rs };
  }

  const payload = {
    fechaMs: hoy.getTime(),
    fechaISO: snapshotId,
    fechaCreacion: Timestamp.now(),
    creadoPor: {
      uid: user?.uid || "",
      email: user?.email || "",
    },
    totales,
    porDeuda,
  };

  await setDoc(doc(db, COBRANZA_SNAPSHOTS, snapshotId), payload);
  return { snapshotId, ...payload };
};

/**
 * Lista los últimos N snapshots ordenados por fecha descendente.
 */
export const listarSnapshotsRecientes = async (n = 12) => {
  const q = query(
    collection(db, COBRANZA_SNAPSHOTS),
    orderBy("fechaMs", "desc"),
    limit(n),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

/**
 * Resuelve la diferencia entre el snapshot más reciente (excluyendo el de HOY)
 * y los items actuales. Devuelve { previo, totalDelta, porDeuda: Map<deudaId, delta> }.
 */
export const calcularDeltas = (snapshots, itemsActuales = []) => {
  if (!snapshots || snapshots.length === 0) return null;

  const hoy = new Date();
  const yyyy = hoy.getFullYear();
  const mm = String(hoy.getMonth() + 1).padStart(2, "0");
  const dd = String(hoy.getDate()).padStart(2, "0");
  const hoyKey = `${yyyy}-${mm}-${dd}`;

  // Snapshot previo = el más reciente que no sea el de hoy
  const previo = snapshots.find((s) => s.id !== hoyKey) || null;
  if (!previo) return null;

  const porDeudaActual = new Map();
  for (const it of itemsActuales) {
    porDeudaActual.set(it.id, {
      sa: Number(it.saldoAtrasado || 0),
      sr: Number(it.saldoRestante || 0),
    });
  }

  const porDeudaDelta = new Map();
  let totalDeltaSA = 0;
  let totalDeltaSR = 0;

  for (const it of itemsActuales) {
    const prev = previo.porDeuda?.[it.id];
    if (!prev) continue;
    const deltaSA = Number(it.saldoAtrasado || 0) - Number(prev.sa || 0);
    const deltaSR = Number(it.saldoRestante || 0) - Number(prev.sr || 0);
    porDeudaDelta.set(it.id, { sa: deltaSA, sr: deltaSR });
    totalDeltaSA += deltaSA;
    totalDeltaSR += deltaSR;
  }

  // Días entre snapshots
  const diasEntre = Math.round(
    (hoy.getTime() - Number(previo.fechaMs || 0)) / 86400000,
  );

  return {
    previo,
    diasEntre,
    totalDeltaSA,
    totalDeltaSR,
    porDeuda: porDeudaDelta,
  };
};
